import {
  CommunicationView,
  communicationTitle,
} from "@/components/communications/CommunicationView.tsx";
import { discardEventProposal } from "@/components/calendars/pendingEventProposal.ts";
import { Loader } from "@/components/loader/Loader.tsx";
import { useEffect } from "react";
import { CanvasEventCard } from "./CanvasEventCard.tsx";
import { CanvasCenteredBody, CanvasModalCard } from "./CanvasModalCard.tsx";
import { useSource } from "./useSource.ts";
import { SavedObjectView, savedObjectServiceName } from "./SavedObjectView.tsx";
import type { SavedObjectDetail } from "@/types/sources.ts";

type SourceData = NonNullable<ReturnType<typeof useSource>["data"]>;
type CommunicationDetail = Extract<SourceData, { type: "communication" }>["detail"];
type EventDetail = Extract<SourceData, { type: "event" }>["detail"];

/** What the modal should present for the current canvas source. */
type ModalSource =
  | { kind: "communication"; detail: CommunicationDetail }
  | { kind: "event"; detail: EventDetail }
  | { kind: "savedObject"; detail: SavedObjectDetail }
  | { kind: "not-viewable" };

/** The stack-layer chrome forwarded to {@link CanvasModalCard} by every variant. */
type ShellProps = {
  container: HTMLElement;
  showBack: boolean;
  isFullscreen: boolean;
  onBack: () => void;
  onClose: () => void;
};

/**
 * The source overlay for the always-mounted calendar. A source still resolving
 * has no card yet and resolves to `null`: the root rests on the calendar, a
 * drilled layer shows a {@link FallbackCard} so it keeps Back/Close.
 */
export const CanvasSourceModal = ({
  sourceId,
  container,
  showBack,
  isFullscreen,
  onBack,
  onClose,
}: {
  /** The targeted source ent id, or undefined when no source is in the canvas. */
  sourceId: string | undefined;
} & ShellProps) => {
  const { data, isLoading, isError } = useSource(sourceId);

  // A drag hand-off aimed at this source can never be claimed once the source
  // fails to resolve — no card mounts to take it. Drop it here rather than leave
  // it to expire, or a card the user opens next would seed from it.
  useEffect(() => {
    if (isError && sourceId) discardEventProposal(sourceId);
  }, [isError, sourceId]);

  if (!sourceId) return null;

  const shellProps: ShellProps = { container, showBack, isFullscreen, onBack, onClose };
  const source = resolveModalSource({ data, isLoading, isError });
  if (!source) return showBack ? <FallbackCard loading={isLoading} {...shellProps} /> : null;

  // An event brings its own card: the detail panel owns a header (editable
  // title, rename, delete) and an unsaved-changes guard the generic shell can't
  // express, so it wires the frame itself rather than rendering into this one.
  if (source.kind === "event") return <CanvasEventCard event={source.detail} {...shellProps} />;

  return (
    <CanvasModalCard title={titleForSource(source)} {...shellProps}>
      <SourceBody source={source} />
    </CanvasModalCard>
  );
};

/**
 * Classify the current canvas source, or `null` while it still resolves (subtype
 * unknown, so no card can be chosen yet). Pure, so the caller's render branches
 * stay plain values.
 */
const resolveModalSource = ({
  data,
  isLoading,
  isError,
}: {
  data: ReturnType<typeof useSource>["data"];
  isLoading: boolean;
  isError: boolean;
}): ModalSource | null => {
  if (isLoading || (!data && !isError)) return null;
  if (isError) return { kind: "not-viewable" };
  if (data?.type === "event") return { kind: "event", detail: data.detail };
  if (data?.type === "communication") return { kind: "communication", detail: data.detail };
  if (data?.type === "connectorEntity") return { kind: "savedObject", detail: data.detail };
  // A renderable-less subtype (invitation / document / calendar).
  return { kind: "not-viewable" };
};

/**
 * The variants that render into the generic shell. An `event` wires its own
 * frame, so excluding it here keeps the `not-viewable` fallback below from
 * silently claiming one if that branch is ever dropped.
 */
type ShellSource = Exclude<ModalSource, { kind: "event" }>;

/** The header title for the current source. */
const titleForSource = (source: ShellSource): string => {
  if (source.kind === "communication") return communicationTitle(source.detail.channel);
  if (source.kind === "savedObject") return savedObjectServiceName(source.detail.service);
  return "Not viewable";
};

/** The source's body, rendered inside the shell's scroll region. */
const SourceBody = ({ source }: { source: ShellSource }) => {
  if (source.kind === "communication") {
    return (
      <div className="px-4 py-4">
        <CommunicationView id={source.detail.sourceId} data={source.detail} density="comfortable" />
      </div>
    );
  }
  if (source.kind === "savedObject") return <SavedObjectView detail={source.detail} />;
  return <CanvasCenteredBody>This item can't be viewed yet.</CanvasCenteredBody>;
};

/**
 * Card for a drilled layer whose source hasn't resolved to a subtype yet, so the
 * layer keeps its Back/Close while it settles.
 */
const FallbackCard = ({ loading, ...shellProps }: { loading: boolean } & ShellProps) => (
  <CanvasModalCard title={loading ? "Loading…" : "Not viewable here"} {...shellProps}>
    <CanvasCenteredBody>
      {loading ? <Loader /> : "This item can't be viewed yet."}
    </CanvasCenteredBody>
  </CanvasModalCard>
);
